
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React from 'react';
import { KeyIcon, SparklesIcon } from './icons';

interface ApiKeyDialogProps {
  onContinue: () => void;
}

const ApiKeyDialog: React.FC<ApiKeyDialogProps> = ({ onContinue }) => {
  const isPlatformSupported = !!window.aistudio;

  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-xl flex items-center justify-center z-[100] p-4">
      <div className="bg-[#141414] border border-gray-800 rounded-3xl shadow-2xl max-w-lg w-full p-10 text-center flex flex-col items-center relative overflow-hidden">
        {/* Decorative background glows */}
        <div className="absolute -top-24 -left-24 w-48 h-48 bg-indigo-600/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-[#D87431]/10 rounded-full blur-3xl" />

        <div className="bg-indigo-600/10 p-5 rounded-2xl mb-8 border border-indigo-500/20">
          <KeyIcon className="w-10 h-10 text-indigo-400" />
        </div>
        
        <h2 className="text-3xl font-black text-white mb-4 uppercase tracking-tighter">
          {isPlatformSupported ? 'Paid API Access Required' : 'Configure Your API Key'}
        </h2>
        
        <div className="flex items-center gap-2 mb-6 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full">
           <SparklesIcon className="w-3 h-3 text-amber-500" />
           <span className="text-[10px] font-bold text-amber-500 uppercase tracking-widest text-center">Exclusive Veo 3.1 Feature</span>
        </div>

        <div className="text-gray-400 mb-8 leading-relaxed text-sm space-y-4">
          <p>
            Veo video generation requires a project with <strong className="text-white">Active Billing</strong> enabled in Google AI Studio.
          </p>
          
          {isPlatformSupported ? (
            <p>
              Click the button below to open the secure project selector. You do not need to paste a key manually; simply select your paid project from the list.
            </p>
          ) : (
            <div className="p-4 bg-gray-900 border border-gray-800 rounded-xl text-left">
              <p className="text-xs font-bold text-indigo-400 uppercase mb-2">Platform Note:</p>
              <p className="text-xs">
                You are accessing this app outside of the AI Studio preview. To use it, you must set your <code className="bg-black px-1 rounded text-white">API_KEY</code> in your deployment environment variables (e.g. Cloudflare or Vite .env).
              </p>
            </div>
          )}
        </div>

        <div className="w-full space-y-4">
          <button
            onClick={onContinue}
            className={`w-full px-6 py-4 font-black rounded-xl transition-all text-sm uppercase tracking-widest shadow-xl active:scale-[0.98] ${
              isPlatformSupported 
                ? 'bg-white text-black hover:bg-gray-200' 
                : 'bg-indigo-600 text-white hover:bg-indigo-500'
            }`}
          >
            {isPlatformSupported ? 'Authenticate & Open Selector' : 'I\'ve Set My Environment Key'}
          </button>
          
          <div className="flex justify-center gap-6">
            <a
              href="https://ai.google.dev/gemini-api/docs/billing"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-indigo-400 text-[10px] font-bold uppercase tracking-widest transition-colors"
            >
              Billing Setup
            </a>
            <a
              href="https://ai.google.dev/gemini-api/docs/pricing#veo-3"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-indigo-400 text-[10px] font-bold uppercase tracking-widest transition-colors"
            >
              Veo Pricing
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiKeyDialog;
